import { lazy, Suspense, useEffect, useState } from "react"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import "./App.css"
import LoadingBackdrop from "./components/LayoutBackdrop"
import { SocketProvider } from "./context/SocketContext.js"
import useAuth from "./hook/useAuth.js"
import useChangeLanguage from "./hook/useChangeLanguage"
import useFormatNumber from "./hook/useFormatNumber.js"
import NotFound from "./pages/NotFound/index.js"
import {
  askForNotificationPermission,
  registerServiceWorker
} from "./pushManager.js"
import {
  FullAccess,
  Role,
  RouteUnAuthorizedSigned
} from "./schemas/validations/auth.js"
import apiTest from "./services/apiTest.js"
import { i18n } from "./translations/i18n.js"

const Agents = lazy(() => import("./pages/Agents"))
const Inbox = lazy(() => import("./pages/Whatsapp/Inbox"))
const Connections = lazy(() => import("./pages/Whatsapp/Connections"))
const EmbeddedSignup = lazy(() => import("./pages/Whatsapp/EmbeddedSignup"))
const EmbeddedInbox = lazy(() => import("./pages/Whatsapp/EmbeddedInbox"))
const Plans = lazy(() => import("./pages/Plans"))
const PlansManagement = lazy(() => import("./pages/Management/Plans"))
const ClientsManagement = lazy(() => import("./pages/Management/Clients"))
const CheckoutSuccess = lazy(() => import("./pages/Checkout/Success"))
const CheckoutError = lazy(() => import("./pages/Checkout/Error"))
const CheckoutPlan = lazy(() => import("./pages/Checkout/Plan"))
const ConfigAbout = lazy(() => import("./pages/ConfigAbout/ConfigAbout"))
const ConfigAddress = lazy(() => import("./pages/ConfigAddress/ConfigAddress"))
const ConfigContact = lazy(() => import("./pages/ConfigContact/ConfigContact"))
const ConfigScript = lazy(() => import("./pages/ConfigScript/ConfigScript"))
const ConfigView = lazy(() => import("./pages/ConfigView/ConfigView"))
const ConfigWhatsapp = lazy(() =>
  import("./pages/ConfigWhatsapp/ConfigWhatsapp")
)
const HelpDesk = lazy(() => import("./pages/HelpDesk"))
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"))
const InternalHelpDesk = lazy(() => import("./pages/InternalHelpDesk"))
const CheckoutTransparent = lazy(() => import("./pages/Checkout/Transparent"))
const FinishRegister = lazy(() => import("./pages/FinishRegister"))
const Forget = lazy(() => import("./pages/Forget/Forget"))
const Login = lazy(() => import("./pages/Login"))
const MyAccount = lazy(() => import("./pages/MyAccount/MyAccount"))
const NewPassWd = lazy(() => import("./pages/NewPassWd/NewPassWd"))
const Register = lazy(() => import("./pages/Register"))
const SocialMedia = lazy(() => import("./pages/SocialMedia/SocialMedia"))
const Users = lazy(() => import("./pages/Users/Users"))

function App() {
  const { setLng, getLng } = useChangeLanguage()
  const [isAuth, setIsAuth] = useState(false)
  const [loading, setLoading] = useState(true)
  const { setToken, setUser, user, setPopupOpen, setDataPopup } = useAuth()
  const { formatNumber } = useFormatNumber()
  const localLng = getLng() ?? "pt"
  const pathname = window.location?.pathname

  const clearStates = () => {
    setUser(null)
    setIsAuth(false)
    setToken(null)
  }

  const RouteWithSocket = ({ element }) => {
    return <SocketProvider>{element}</SocketProvider>
  }

  const hasPermission = (user, roles) => {
    if (!roles) return true
    return roles.includes(user?.access)
  }

  const ProtectedRoute = ({ element, roles }) => {
    return isAuth && hasPermission(user, roles) ? (
      element
    ) : (
      <Navigate to={isAuth ? "/painel/dashboard" : `/?redirect=${pathname}`} />
    )
  }

  const loadVerifyPlanExpired = invoiceAccount => {
    setPopupOpen(true)
    setDataPopup({
      title: `${i18n.t("popupInfo.invoiceExpiredTitle")}`,
      subtitle: `${i18n.t("popupInfo.invoiceExpiredSubtitle")}`,
      items: [],
      link: `/checkout/${invoiceAccount}`
    })
  }

  const loadChangePlan = () => {
    setPopupOpen(true)
    setDataPopup({
      title: `${i18n.t("pages.popupChange.title")}`,
      subtitle: `${i18n.t("pages.popupChange.subtitle")}`,
      items: [],
      link: `/planos`
    })
  }

  const checkAuth = async () => {
    try {
      const authToken = localStorage.getItem("token")

      if (!authToken || authToken === "") return clearStates()

      apiTest.defaults.headers.common["Authorization"] = authToken

      const result = await apiTest.get("/verify")
      const response = result?.data
      const userSelected = response?.user
      const endDate = userSelected?.endDate
      const endDateFormatted = endDate && endDate !== "0000-00-00" && endDate
      const canceledDate = userSelected?.canceledDate

      const currentDate =
        userSelected?.toDay && new Date(`${userSelected?.toDay}T23:59:00`)

      const receivedDate =
        endDateFormatted && new Date(`${endDateFormatted}T23:59:00`)
      const isPlanExpired = receivedDate && currentDate > receivedDate

      const invoiceAccount = userSelected?.invoiceAccount
      const hasCanceled =
        canceledDate && canceledDate !== "0000-00-00" && !userSelected?.plan

      if (result?.status !== 200 || response?.error) {
        localStorage.removeItem("token")
        return clearStates()
      }

      if (hasCanceled) {
        loadChangePlan()
      }

      if (invoiceAccount && !!isPlanExpired) {
        loadVerifyPlanExpired(invoiceAccount)
      }

      const plan = userSelected?.plan

      const userNow = {
        userId: userSelected?.userId ?? "",
        firstname: userSelected?.firstname ?? "",
        lastname: userSelected?.lastname ?? "",
        access: userSelected?.access ?? "",
        photo: userSelected?.photo ?? "",
        email: userSelected?.email ?? "",
        invoiceAccount: userSelected?.invoiceAccount,
        startDate: userSelected?.startDate,
        endDate: userSelected?.endDate,
        canceledDate: userSelected?.canceledDate,
        username: userSelected?.username ?? "",
        accountType: userSelected?.accountType ?? "",
        ddi: userSelected?.ddi ?? "",
        phone: userSelected?.phone ?? "",
        plan: {
          id: plan?.id ?? "",
          name: plan?.name ?? "",
          stripeId: plan?.stripeId ?? "",
          value: plan?.value ?? "",
          maxProperties: formatNumber(plan?.maxProperties || -1),
          maxPhotos: formatNumber(plan?.maxPhotos || -1)
        }
      }

      const resToken = response?.token

      setUser(userNow)
      setIsAuth(true)
      setToken(resToken)

      const hasAccess = RouteUnAuthorizedSigned.includes(pathname)

      if (hasAccess) return window.location.replace("/painel/dashboard")
    } catch (error) {
      console.error(error)
      clearStates()
    } finally {
      setLoading(false)
    }
  }

  const handlerSubscribe = async () => {
    const permission = await askForNotificationPermission()

    if (permission === "granted") {
      await registerServiceWorker({ userId: user?.userId })
    }
  }

  useEffect(() => {
    if (!user) return
    handlerSubscribe()
  }, [user])

  useEffect(() => {
    setLng(localLng)
    checkAuth()
  }, [])

  if (loading) return <LoadingBackdrop lang={localLng} />

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingBackdrop lang={localLng} />}>
        <Routes>
          <Route path='/' element={<Login />} />
          <Route path='/registro' element={<Register />} />
          <Route path='/recuperar-conta' element={<Forget />} />
          <Route path='/finalizar-cadastro' element={<FinishRegister />} />
          <Route
            path='/checkout-sucesso/:paymentId'
            element={<CheckoutSuccess />}
          />
          <Route path='/checkout-erro' element={<CheckoutError />} />
          <Route path='/checkout-plan/:uuid' element={<CheckoutPlan />} />
          <Route path='/checkout/:token' element={<CheckoutTransparent />} />
          <Route
            path='/planos'
            element={<ProtectedRoute element={<Plans />} roles={FullAccess} />}
          />

          <Route
            path='/recuperar-senha/:email/:forget'
            element={<NewPassWd />}
          />
          <Route
            path='/painel/dashboard'
            element={
              <ProtectedRoute element={<Dashboard />} roles={FullAccess} />
            }
          />

          <Route
            path='/painel/suporte'
            element={
              <ProtectedRoute element={<HelpDesk />} roles={FullAccess} />
            }
          />
          <Route
            path='/painel/suporte/:id'
            element={
              <ProtectedRoute
                element={<InternalHelpDesk />}
                roles={FullAccess}
              />
            }
          />

          <Route
            path='/painel/assistentes'
            element={<ProtectedRoute element={<Agents />} roles={FullAccess} />}
          />

          <Route
            path='/painel/usuarios'
            element={
              <ProtectedRoute element={<Users />} roles={[Role.ADMIN]} />
            }
          />
          <Route
            path='/painel/planos'
            element={
              <ProtectedRoute
                element={<PlansManagement />}
                roles={[Role.ADMIN, Role.FINANCEIRO]}
              />
            }
          />
          <Route
            path='/painel/clientes'
            element={
              <ProtectedRoute
                element={<ClientsManagement />}
                roles={FullAccess}
              />
            }
          />

          <Route
            path='/painel/minha-conta'
            element={
              <ProtectedRoute element={<MyAccount />} roles={FullAccess} />
            }
          />

          <Route
            path='/painel/config/social-media'
            element={
              <ProtectedRoute element={<SocialMedia />} roles={[Role.ADMIN]} />
            }
          />
          <Route
            path='/painel/whatsapp/conexoes'
            element={
              <ProtectedRoute
                element={<RouteWithSocket element={<Connections />} />}
                roles={[Role.ADMIN]}
              />
            }
          />
          <Route
            path='/painel/conectar-whatsapp-ebedded'
            element={
              <ProtectedRoute
                element={<EmbeddedSignup />}
                roles={[Role.ADMIN]}
              />
            }
          />
          <Route
            path='/painel/whatsapp-embedded/:connectionId/conversas/:conversationId?'
            element={
              <ProtectedRoute
                element={<EmbeddedInbox />}
                roles={[Role.ADMIN]}
              />
            }
          />
          <Route
            path='/painel/whatsapp/:id/conversas/:conversationId?'
            element={
              <ProtectedRoute
                element={<RouteWithSocket element={<Inbox />} />}
                roles={[Role.ADMIN]}
              />
            }
          />
          <Route
            path='/painel/config/address'
            element={
              <ProtectedRoute
                element={<ConfigAddress />}
                roles={[Role.ADMIN]}
              />
            }
          />
          <Route
            path='/painel/config/contact'
            element={
              <ProtectedRoute
                element={<ConfigContact />}
                roles={[Role.ADMIN]}
              />
            }
          />

          <Route
            path='/painel/config/about'
            element={
              <ProtectedRoute element={<ConfigAbout />} roles={[Role.ADMIN]} />
            }
          />
          <Route
            path='/painel/config/whatsapp'
            element={
              <ProtectedRoute
                element={<ConfigWhatsapp />}
                roles={[Role.ADMIN]}
              />
            }
          />
          <Route
            path='/painel/config/view'
            element={
              <ProtectedRoute element={<ConfigView />} roles={[Role.ADMIN]} />
            }
          />
          <Route
            path='/painel/config/scripts'
            element={
              <ProtectedRoute element={<ConfigScript />} roles={[Role.ADMIN]} />
            }
          />

          <Route
            path='/painel'
            element={
              <ProtectedRoute element={<Dashboard />} roles={FullAccess} />
            }
          />

          <Route path='*' element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default App
